import axios, { AxiosRequestConfig } from "axios";
import { message } from "antd";
import { apiV1Endpoint } from "../api/v1";
import { getAccessToken, getLoginResponse, getRefreshToken, saveSession, clearSession } from "./session";

// Dedicated instance for refresh — no interceptors, prevents infinite loop
const refreshClient = axios.create();

// ── Queue pattern: ensures only ONE refresh call fires for concurrent 401s ─────
type QueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void };

let isRefreshing = false;
let failedQueue: QueueEntry[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token!)));
  failedQueue = [];
};
// ─────────────────────────────────────────────────────────────────────────────

const fetchNewToken = async (refreshToken: string) => {
  const { data } = await refreshClient.post(
    `${apiV1Endpoint}/refresh-token`,
    { refreshToken },
    { headers: { appname: "institutionalLandlordPortal" } },
  );
  return data.result as { token: string; refreshToken: string };
};

const applyToken = (token: string) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

const handleSessionExpired = () => {
  clearSession();
  message.info({ content: "Your session has expired. Please log in again.", key: "session-expired" });
  setTimeout(() => {
    window.location.href = "/login";
  }, 500);
};

// ── Request interceptor ────────────────────────────────────────────────────────

axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (!config.url?.includes("translation.googleapis.com")) {
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    config.headers["appname"] = "institutionalLandlordPortal";
  }
  return config;
});

// ── Response interceptor ───────────────────────────────────────────────────────

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (status === 403) message.error("You are not authorized to perform this action");
    if (status === 500) message.error("Internal server error");

    if (status !== 401) return Promise.reject(error);

    // A refresh is already in progress — queue this request and wait
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${token}` };
        return axios(originalRequest);
      });
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    isRefreshing = true;

    try {
      const result = await fetchNewToken(refreshToken);
      saveSession({ ...getLoginResponse(), ...result });
      applyToken(result.token);
      processQueue(null, result.token);
      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${result.token}` };
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      handleSessionExpired();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axios;
